import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import styles from "./BannerVideo.module.css";

import { useRouter } from "next/router";
import { FaStar } from "react-icons/fa";
import { CloudinaryImage } from "../../Atoms/Cloudinary";
import SearchBar from "../SearchBar";

interface HomeBannerVideoProps {
  title: string;
  subtitle: string;
  videoSrc: string;
  coverPoster: string;
}

export default function HomeBannerVideo({
  title,
  subtitle,
  videoSrc,
  coverPoster,
}: HomeBannerVideoProps) {
  const [canRenderVid, setCanRenderVid] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const router = useRouter();

  const playMountedVideo = useCallback(() => {
    if (canRenderVid) {
      setTimeout(() => {
        if (
          canRenderVid &&
          videoRef.current &&
          videoRef.current.readyState >= 3 &&
          !isPlaying &&
          !isLoaded
        ) {
          videoRef.current.setAttribute("webkit-playsinline", "true");
          videoRef.current.play();
          setIsPlaying(true);
          setIsLoaded(true);
        }
      }, 3000);

      if (videoRef.current) {
        videoRef.current.onended = () => {
          setIsPlaying(false);
        };
      }
    }
  }, [canRenderVid, isPlaying, isLoaded]);

  useEffect(() => {
    setCanRenderVid(true);
    playMountedVideo();
  }, [playMountedVideo]);

  useEffect(() => {
    setIsPlaying(false);
    setIsMuted(true);
    setIsLoaded(false);
  }, [router]);

  useEffect(() => {
    playMountedVideo();
  }, [router, playMountedVideo]);

  return (
    <div className={styles["HomeBannerContainer"]}>
      <div className={styles["HomeBannerContainerBackground"]}>
        <CloudinaryImage src={coverPoster} transformations={["h_900"]} />
      </div>

      {canRenderVid ? (
        <div
          className={
            isPlaying ? "VideoContainer fadeIn" : "VideoContainer fadeOut"
          }
        >
          <video
            key={videoSrc}
            ref={videoRef}
            muted={isMuted}
            playsInline
            autoPlay
            loop
            preload="none"
          >
            <source src={`${videoSrc}.mp4`} type="video/mp4" />
          </video>
        </div>
      ) : null}

      <div className={styles["HomeBannerPanel"]} />
      <div className={styles["VideoMssgContainer"]}>
        <div className={styles.HomeVideoMessage}>
          <div className={styles.MetaMessage}>
            <h1>
              {title.split("\n").map((line, index) => (
                <Fragment key={index}>
                  {line}
                  {index < title.split("\n").length - 1 && <br />}
                </Fragment>
              ))}
            </h1>
            <h2>{subtitle}</h2>
          </div>
        </div>
        <div className={styles["HomeSearchBar"]}>
          <SearchBar variant="responsive" reference="home-search-bar" />
        </div>
        <div className={styles["TripAdvisorBadge"]}>
          <svg
            width="60"
            height="36"
            viewBox="0 0 60 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_6_2530)">
              <path
                d="M17.5963 20.6501C17.5963 22.0132 16.4718 23.1377 15.1087 23.1377C13.7457 23.1377 12.6212 22.0132 12.6212 20.6501C12.6212 19.2871 13.7457 18.1626 15.1087 18.1626C16.4718 18.1626 17.5963 19.2871 17.5963 20.6501ZM44.9936 18.1626C43.6305 18.1626 42.506 19.2871 42.506 20.6501C42.506 22.0132 43.6305 23.1377 44.9936 23.1377C46.3566 23.1377 47.4811 22.0132 47.4811 20.6501C47.4811 19.2871 46.3907 18.1626 44.9936 18.1626ZM54.2623 32.6109C47.7878 37.6882 38.3828 36.5637 33.3054 30.0551L30.1023 34.8599L26.9332 30.0892C21.924 36.3252 12.8938 37.5178 6.4534 32.7812C-0.191459 27.9083 -1.62266 18.5715 3.28431 11.9607C2.63686 9.81389 1.64865 7.83747 0.35376 6.03143H9.69064C15.8244 2.2149 22.9122 0.272557 30.1023 0.442938C37.122 0.306633 44.0394 2.24898 50.0028 5.96328H59.953C58.624 7.76932 57.6358 9.77981 57.0225 11.9266C61.8613 18.4352 60.6345 27.6017 54.2623 32.6109ZM27.2058 20.7864C27.2058 14.2097 21.8558 8.85976 15.2791 8.85976C8.70243 8.85976 3.35246 14.2097 3.35246 20.7864C3.35246 27.3631 8.70243 32.7131 15.2791 32.7131C21.8558 32.679 27.2058 27.3631 27.2058 20.7864ZM30.1704 19.696C30.7497 12.097 36.9516 6.13366 44.5847 5.9292C40.0184 3.95279 35.0774 2.96458 30.1364 3.0668C25.1612 3.03273 20.2202 3.98686 15.6199 5.89513C23.287 6.03143 29.5911 11.9948 30.1704 19.696ZM56.2387 16.6973C53.9556 10.5295 47.1063 7.3604 40.9385 9.64351C34.7366 11.9266 31.6016 18.7759 33.8507 24.9437C36.1338 31.1115 42.9831 34.2806 49.1509 31.9975C55.3527 29.7144 58.4877 22.8991 56.2387 16.6973ZM22.4692 20.6501C22.4692 24.7393 19.1638 28.0447 15.0747 28.0106C10.9855 27.9765 7.71422 24.7052 7.71422 20.616C7.71422 16.5269 11.0196 13.2556 15.1087 13.2556C19.1638 13.2897 22.4692 16.561 22.4692 20.6501ZM19.9476 20.6501C19.9476 17.9581 17.7667 15.8113 15.1087 15.8113C12.4508 15.8113 10.2699 17.9922 10.2699 20.6501C10.2699 23.3081 12.4508 25.4889 15.1087 25.4889C17.7667 25.4549 19.9135 23.3081 19.9476 20.6501ZM52.3881 20.6501C52.3881 24.7393 49.0827 28.0106 45.0276 28.0106C40.9726 28.0106 37.6672 24.7052 37.6672 20.6501C37.6672 16.5951 40.9726 13.2897 45.0276 13.2897C49.0827 13.2897 52.3881 16.561 52.3881 20.6501ZM49.8324 20.6501C49.8324 17.9922 47.6515 15.8113 44.9936 15.8113C42.3356 15.8113 40.1548 17.9922 40.1548 20.6501C40.1548 23.3081 42.3356 25.4889 44.9936 25.4889C47.6856 25.4549 49.8324 23.3081 49.8324 20.6501Z"
                fill="white"
              />
            </g>
            <defs>
              <clipPath id="clip0_6_2530">
                <rect
                  width="59.9741"
                  height="35.78"
                  fill="white"
                  transform="translate(0.0129395)"
                />
              </clipPath>
            </defs>
          </svg>
          <div className={styles["TripFameMsg"]}>
            <FaStar color="#fff" />
            <FaStar color="#fff" />
            <FaStar color="#fff" />
            <FaStar color="#fff" />
            <FaStar color="#fff" />
            <p>As recommended by 99% of TripAdvisor users</p>
          </div>
        </div>
      </div>

      <div className={styles["BannerBottomShadow"]} />
    </div>
  );
}
